---
title: Signing the agents in
lede: How Claude Code and Codex sign in, what reaches a session of each, and the settings they share.
order: 4
part: Get started
---

Airlock runs two agents, Claude Code and Codex; a session uses the one whose model you pick. Their settings are under **Agents** in **Settings**: a page per agent, **All agents**, and **Skills, MCP & plugins**.

### Claude Code

![The Claude Code page](media/settings-claude-code.webp)

A micro-VM cannot use your own login, so Airlock keeps a subscription token for it.

1. In **Settings › Claude Code**, press **Set up token** (**Re-run** once you have one).
2. Approve the page your browser opens and copy its code.
3. Paste it into **Code** and press **Finish**.

**Forget** removes the token; running micro-VM sessions lose API access at their next request.

:::note The token never enters a session
It stays in your OS keychain and is swapped into each request on its way out. The agent in the micro-VM is signed in without ever holding it.
:::

### Codex

Codex reuses this computer's login: run `codex login`, and the page says what it found.

- **An OpenAI API key** stays on your computer, swapped in on the way out like the Claude token.
- **A ChatGPT login** is copied into the micro-VM. Use an API key if the credential must not enter the sandbox.

A session on this computer uses your own login for either agent.

### Turning an agent off

**Use Claude Code** and **Use Codex** remove that agent's models from every picker. The last agent left, or one a workflow step uses, cannot be turned off; the switch says why.

### Each agent's settings

Each agent's page lists about thirty settings from its own config (`~/.claude/settings.json`, `~/.codex/config.toml`).

- Filter with **All** or **Set here**, or search. **Clear all** unsets everything you set.
- An unset row shows where its value comes from: **your config**, **default** or **not set**. **Set** takes it over; **Reset** gives it back.
- Rows marked **set by Airlock**, such as the model and credentials, are decided per session.

Your values are written into the agent's config each time a session starts.

### All agents

![The All agents page](media/settings-agent.webp)

| Setting | What it does | Default |
|---|---|---|
| Run autonomously | The agent skips approvals: in a micro-VM, the VM is the boundary; on this computer, the agent's own automatic mode applies. Off, it asks before each step | On |
| Agent panel runs in | Where the [Agent panel](/agent-panel)'s turns run: **Micro-VM**, thrown away after each turn, or **This computer** | Micro-VM |
| Reuse the agent's config from this computer | **Copy and sanitize it** copies `~/.claude` or `~/.codex` in, without credentials or this computer's paths. **Start from nothing** starts clean | Copy |
| Mask personal data | Replaces personal data in what Airlock sends an agent with placeholders. **Names to mask** adds your own terms. See [Settings](/settings) | Off |
| Agent version | Update to the newest release on start, or keep the one the session image was built with | Newest |
| Idle fallback | Seconds without activity before a working session shows as waiting (5–600) | 20 |
| Language | Language of commit messages, workflow steps, the Change Map and pull request comments, each set on its own: English or Français | English |
| Let the agent drive Preview | Lets the agent drive the browser and API console. **…including saved logins** also lets it sign a page in with a login from the vault. See [Agent MCP](/agent-preview) | On |

### Skills, MCP & plugins

What a session's agent gets beyond its CLI — skills, MCP servers and plugins — is set on its own page. See [Skills, MCP & plugins](/extensions).

